/* Native server-rendered PDP enhancement. Variant HTML is rendered by Shopify, not reconstructed from stale prices. */
if (!customElements.get('napoleon-pdp-v2')) customElements.define('napoleon-pdp-v2', class extends HTMLElement {
  static optionCache = new Map();
  optionUrls(variant) {
    const url=new URL(this.dataset.productUrl,location.origin);
    const current=new URL(location.href);
    for(const key of ['view','preview_theme_id','_fd','pb'])if(current.searchParams.has(key))url.searchParams.set(key,current.searchParams.get(key));
    if(this.dataset.previewThemeId)url.searchParams.set('preview_theme_id',this.dataset.previewThemeId);
    url.searchParams.set('variant',variant);
    const fetchUrl=new URL(url);fetchUrl.searchParams.set('section_id',this.dataset.sectionId);
    return {url,fetchUrl};
  }
  optionHtml(fetchUrl) {
    const cache=this.constructor.optionCache,key=String(fetchUrl),now=Date.now();
    const cached=cache.get(key);
    if(cached&&now-cached.created<30000)return cached.promise;
    const entry={created:now,promise:null};
    entry.promise=fetch(fetchUrl,{headers:{Accept:'text/html'}}).then(async response=>{
      // Shopify can reject a dynamic section ID on an alternate product template.
      // Fetch the same selected product HTML without navigating the current page.
      if(response.status===404){
        const fallback=new URL(fetchUrl);
        if(fallback.searchParams.has('section_id')){
          fallback.searchParams.delete('section_id');
          response=await fetch(fallback,{headers:{Accept:'text/html'}});
        }
      }
      if(!response.ok)throw Error('Variant could not be loaded');
      return response.text();
    }).catch(error=>{if(cache.get(key)===entry)cache.delete(key);throw error;});
    cache.set(key,entry);
    if(cache.size>12)cache.delete(cache.keys().next().value);
    return entry.promise;
  }
  warmOption(event) {
    const choice=event.target.closest('[data-pdp-variant]');
    if(!choice||!this.contains(choice)||choice.disabled||choice.dataset.pdpVariant===this.dataset.variantId)return;
    this.optionHtml(this.optionUrls(choice.dataset.pdpVariant).fetchUrl).catch(()=>{});
  }
  setPendingOption(choice) {
    this.querySelectorAll('[data-pdp-variant]').forEach(button=>{
      button.toggleAttribute('data-option-loading',button===choice);
      if(button===choice)button.setAttribute('aria-busy','true');
      else button.removeAttribute('aria-busy');
    });
    this.querySelectorAll('[data-add-setup],.np2-product-form button[name=add]').forEach(button=>{
      button.disabled=!!choice||this.dataset.available!=='true';
    });
    this.updateSticky();
  }
  async warmAvailableOptions() {
    const variants=[...new Set([...this.querySelectorAll('[data-pdp-variant]')]
      .filter(button=>!button.disabled&&button.dataset.pdpVariant!==this.dataset.variantId)
      .map(button=>button.dataset.pdpVariant))];
    const worker=async()=>{
      while(variants.length&&this.isConnected&&!this.controller.signal.aborted){
        const variant=variants.shift();
        try{await this.optionHtml(this.optionUrls(variant).fetchUrl)}catch{}
      }
    };
    await Promise.all([worker(),worker()]);
  }
  connectedCallback() {
    this.controller = new AbortController();
    this.addEventListener('click', this.onClick.bind(this), {signal:this.controller.signal});
    for(const type of ['pointerover','focusin','touchstart'])this.addEventListener(type,this.warmOption.bind(this),{signal:this.controller.signal,passive:true});
    const header=document.querySelector('.ho-header');
    if(header){
      const updateClearance=()=>this.style.setProperty('--np2-header-clearance',`${Math.ceil(header.getBoundingClientRect().height)+20}px`);
      this.headerObserver=new ResizeObserver(updateClearance);
      this.headerObserver.observe(header);updateClearance();
    }
    this.warmTimer=setTimeout(()=>this.warmAvailableOptions(),300);
    this.loadBenefits();
    this.mountSticky();
    this.mountVideos();
    this.addEventListener('submit',e=>{if(e.target.matches('.np2-product-form')){e.preventDefault();this.addItems(false)}},{signal:this.controller.signal});
    this.addEventListener('change',e=>{if(e.target.matches('[data-addon-sku]'))this.updateSetup()},{signal:this.controller.signal});
    this.updateSetup();
    let start;const main=this.querySelector('.np2-main-image');
    main?.addEventListener('pointerdown',e=>{if(e.button!==0)return;start={x:e.clientX,y:e.clientY};main.setPointerCapture(e.pointerId)},{signal:this.controller.signal});
    main?.addEventListener('pointerup',e=>{if(start&&Math.abs(e.clientX-start.x)>45&&Math.abs(e.clientY-start.y)<45)this.nextImage(e.clientX<start.x?1:-1);start=null},{signal:this.controller.signal});
    main?.addEventListener('pointercancel',()=>{start=null},{signal:this.controller.signal});
    this.querySelector('.np2-gallery')?.addEventListener('keydown',e=>{if(['ArrowLeft','ArrowRight'].includes(e.key)){e.preventDefault();this.nextImage(e.key==='ArrowRight'?1:-1)}},{signal:this.controller.signal});
    this.updateDelivery();
    this.querySelectorAll('.np2-service details,details.np2-service,.np2-addon-details').forEach(el=>{
      el.addEventListener('pointerenter',e=>{if(e.pointerType==='mouse')el.open=true},{signal:this.controller.signal});
      el.querySelector('summary')?.addEventListener('click',e=>{if(e.pointerType==='mouse'){e.preventDefault();el.open=true}},{signal:this.controller.signal});
      el.addEventListener('pointerleave',e=>{if(e.pointerType==='mouse'&&!el.contains(document.activeElement))el.open=false},{signal:this.controller.signal});
      el.addEventListener('focusout',e=>{if(!el.contains(e.relatedTarget)&&!el.matches(':hover'))el.open=false},{signal:this.controller.signal});
      el.addEventListener('keydown',e=>{if(e.key==='Escape'){el.open=false;el.querySelector('summary').focus()}},{signal:this.controller.signal});
    });
  }
  preserveOptionPosition(choice, top) {
    // Shop Pay and gallery media may settle after the synchronous HTML replacement.
    // Stop immediately on new user input so restoration never fights intentional scrolling.
    const controller=new AbortController();
    let frame;
    const restore=()=>{
      if(!this.isConnected||controller.signal.aborted)return;
      const delta=choice.getBoundingClientRect().top-top;
      if(Math.abs(delta)>0.5)window.scrollBy({top:delta,left:0,behavior:'instant'});
    };
    const observer=new ResizeObserver(()=>{cancelAnimationFrame(frame);frame=requestAnimationFrame(restore)});
    const stop=()=>{controller.abort();observer.disconnect();cancelAnimationFrame(frame);clearTimeout(timer)};
    const timer=setTimeout(stop,2000);
    for(const type of ['wheel','touchstart','pointerdown','keydown'])window.addEventListener(type,stop,{capture:true,passive:true,signal:controller.signal});
    for(const element of this.querySelectorAll('.np2-identity,.np2-gallery,.np2-finance'))observer.observe(element);
    frame=requestAnimationFrame(restore);
    this.controller.signal.addEventListener('abort',stop,{once:true});
  }
  async mountVideos() {
    try {
      const {mountPdpVideo}=await import(this.dataset.uiModule);
      if(!this.isConnected)return;
      this.videoUnmounts=[...this.querySelectorAll('[data-np2-video]')].map(host=>{
        const unmount=mountPdpVideo(host,{youtubeId:host.dataset.np2Video,title:host.dataset.videoTitle,description:'',pauseOffscreen:true});
        host.classList.add('np2-video--enhanced');
        return unmount;
      });
    } catch { /* Native embedded video remains available if enhancement cannot load. */ }
  }
  async mountSticky() {
    try {
      const {mountPdpSticky}=await import(this.dataset.uiModule);
      if(!this.isConnected)return;
      this.sticky=mountPdpSticky(this.querySelector('[data-sticky-host]'));
      this.updateSticky();
    } catch { /* The server-rendered purchase form remains available. */ }
  }
  async updateSticky() {
    if(!this.sticky)return;
    try {
      const lines=await this.selectedLines();if(!this.isConnected)return;
      const accessories=new Map([...this.querySelectorAll('[data-addon-sku]')].map(el=>[el.dataset.addonId,el.dataset.addonSku]));
      const skuLines=lines.map(line=>({...line,sku:String(line.id)===this.dataset.variantId?this.dataset.sku:accessories.get(String(line.id))}));
      this.sticky.render({title:this.dataset.title,sku:this.dataset.sku,quantity:1,lines:skuLines,hasOptions:!!this.querySelector('[data-pdp-variant]'),hasAccessories:!!this.querySelector('[data-addon-sku]'),busy:!!(this.adding||this.requesting),available:this.dataset.available==='true',onAdd:()=>this.addItems(true),cartUrl:this.dataset.cartUrl,variantId:sku=>sku===this.dataset.sku?this.dataset.variantId:[...accessories].find(([,value])=>value===sku)?.[0],rootSelector:'napoleon-pdp-v2',purchaseSelector:'.np2-purchase',editTarget:()=>{const target=this.querySelector('.np2-options')||this.querySelector('.np2-setup')||this.querySelector('.np2-buy');const header=document.querySelector('.ho-header');target.style.scrollMarginTop=Math.ceil(Math.max(0,(header?.getBoundingClientRect().bottom||0)-(parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop)||0))+20)+'px';target.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth',block:'start'});target.querySelector('button,input,summary')?.focus({preventScroll:true});}});
    } catch { /* Unavailable selections cannot produce a purchasable bundle. */ }
  }
  money(cents) {return new Intl.NumberFormat('en-US',{style:'currency',currency:this.dataset.currency||'USD'}).format(cents/100);}
  async selectedLines(includeAccessories=true) {
    const {setupLines}=await import(this.dataset.cartModule);
    const allowed=[...this.querySelectorAll('[data-addon-sku]')].map(el=>({sku:el.dataset.addonSku,id:el.dataset.addonId,unitCents:Number(el.dataset.unitCents),quantityPerParent:Number(el.dataset.quantityPerParent),available:!el.disabled}));
    const selected=includeAccessories?[...this.querySelectorAll('[data-addon-sku]:checked')].map(el=>el.dataset.addonSku):[];
    return setupLines({id:this.dataset.variantId,available:this.dataset.available==='true',unitCents:Number(this.dataset.priceCents)},selected,allowed);
  }
  async updateSetup() {
    this.updateSticky();
    try {const lines=await this.selectedLines();const {setupTotal}=await import(this.dataset.cartModule);
      this.querySelectorAll('[data-setup-total]').forEach(el=>el.textContent=this.money(setupTotal(lines)));
      this.querySelectorAll('[data-addon-count]').forEach(el=>el.textContent=lines.length-1);
      this.querySelectorAll('[data-addon-noun]').forEach(el=>el.textContent=lines.length===2?'extra':'extras');
    } catch { /* Unavailable variants retain their server-rendered price and disabled purchase controls. */ }
  }
  async addItems(includeAccessories) {
    if(this.adding||this.requesting)return;this.adding=true;this.updateSticky();
    const buttons=[...this.querySelectorAll('[data-add-setup],.np2-product-form button[name=add]')];
    buttons.forEach(b=>{b.disabled=true;b.setAttribute('aria-busy','true')});
    const notice=this.querySelector('[data-pdp-status]');
    try {
      const lines=await this.selectedLines(includeAccessories);const {confirmsAdded}=await import(this.dataset.cartModule);
      const response=await fetch((window.Shopify?.routes?.root||'/')+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:lines.map(({id,quantity})=>({id,quantity}))})});
      const result=await response.json();
      if(!response.ok)throw Error(result.description||'These items could not be added. Please try again.');
      if(!confirmsAdded(result,lines))throw Error('Please review your cart to confirm which items were added.');
      notice.textContent=lines.length===1?'1 item added to your cart.':lines.length+' items added to your cart.';
      document.dispatchEvent(new CustomEvent('cart:refresh',{detail:{source:'napoleon-pdp-v2'}}));
    } catch(error){notice.textContent=error.message;}
    finally {this.adding=false;this.updateSticky();buttons.forEach(b=>{b.disabled=this.dataset.available!=='true';b.removeAttribute('aria-busy')});this.flushQueuedOption();}
  }
  flushQueuedOption() {
    const choice=this.queuedChoice;this.queuedChoice=null;
    if(choice && this.isConnected)this.onClick({target:choice,preventDefault(){}});
  }
  showImage(button) {
    this.querySelector('.np2-main-image').src=button.dataset.gallerySrc;
    this.querySelectorAll('[data-gallery-src]').forEach(el=>el.setAttribute('aria-pressed',String(el===button)));
  }
  nextImage(step) {
    const buttons=[...this.querySelectorAll('[data-gallery-src]')];if(!buttons.length)return;
    const index=buttons.findIndex(el=>el.getAttribute('aria-pressed')==='true');this.showImage(buttons[(index+step+buttons.length)%buttons.length]);
  }
  async updateDelivery() {
    const {shippingPresentation}=await import('./pdp-shipping-policy.js?v=free-delivery-all-brands');
    const node=this.querySelector('[data-delivery-date]');if(!node)return;
    const data=JSON.parse(document.getElementById('hop-purchase-data').textContent);
    const variant=data.variants?.[this.dataset.sku];
    const arrival=shippingPresentation({...data.defaults,...data.product,...variant?.benefits,vendor:data.vendor||'Napoleon',shipping_available:this.dataset.available==='true'}).delivery_date||'';
    this.querySelectorAll('[data-delivery-date]').forEach(el=>{el.textContent=arrival;el.hidden=!arrival;});
    const stock=this.querySelector('.np2-identity [data-hop-stock-status]');
    if(stock)stock.hidden=!(arrival && this.dataset.available==='true');
  }
  async loadBenefits() {
    try {
      const {purchaseBenefitsData}=await import(this.dataset.purchaseModule);
      const data=JSON.parse(document.getElementById('hop-purchase-data').textContent);
      const benefits=purchaseBenefitsData(data,this.dataset.category,this.dataset.productUrl.split('/').pop(),this.dataset.sku,null);
      benefits.shipping_note=String(benefits.shipping_note||'').replaceAll('Most products ship within 24 hours','Products ship within 24 hours');
      for(const key of ['shipping_summary','shipping_note','shipping_time','returns_summary']){
        this.querySelectorAll('[data-'+key.replaceAll('_','-')+']').forEach(node=>{node.textContent=benefits[key]||'';node.hidden=!benefits[key]});
      }
      this.querySelectorAll('[data-service=shipping] strong,[data-shipping-heading]').forEach(title=>title.textContent=benefits.shipping_heading);
    } catch { this.querySelectorAll('.np2-service-pop p').forEach(p=>p.hidden=true); }
  }
  disconnectedCallback() { clearTimeout(this.warmTimer); this.headerObserver?.disconnect(); this.controller?.abort(); this.request?.abort(); this.sticky?.unmount(); this.videoUnmounts?.forEach(unmount=>unmount()); }
  async onClick(event) {
    const addonRow=event.target.closest('.np2-addon');
    if(addonRow && !event.target.closest('input,label,a,button,.np2-addon-details,summary')){
      const checkbox=addonRow.querySelector('[data-addon-sku]');
      if(checkbox && !checkbox.disabled)checkbox.click();
      return;
    }
    const planning=event.target.closest('[data-add-comparison],[data-save-project]');
    if(planning){
      planning.disabled=true;
      const notice=this.querySelector('[data-planning-status]');
      try {
        const api=await import(this.dataset.uiModule);
        let result;
        if(planning.matches('[data-add-comparison]'))result=api.addPdpComparison(this.dataset.sku);
        else {const lines=await this.selectedLines();const accessories=new Map([...this.querySelectorAll('[data-addon-sku]')].map(el=>[el.dataset.addonId,el.dataset.addonSku]));const items=lines.map(line=>({sku:line.id===this.dataset.variantId?this.dataset.sku:accessories.get(line.id),quantity:line.quantity,title:this.dataset.title,href:this.dataset.productUrl}));if(this.dataset.partsProjectManifest){const core=await import(this.dataset.partsCategoryCore);const response=await fetch(this.dataset.partsProjectManifest);if(!response.ok)throw Error('Project registry unavailable');const manifest=await response.json();result=core.saveGrillProjectItems(items,manifest.configurations);}else result=api.savePdpProject(items);}
        notice.textContent=planning.matches('[data-save-project]') && result.ok
          ? 'Saved to your outdoor project in this browser. Open your projects to manage your selections.'
          : result.message;
      } catch {notice.textContent='This selection could not be saved. Please try again.';}
      finally{planning.disabled=false;}
      return;
    }
    if(event.target.closest('[data-add-setup]')){this.addItems(true);return;}
    const thumb=event.target.closest('[data-gallery-src]');if(thumb){this.showImage(thumb);return;}
    if(event.target.closest('[data-contact-open]')){const dialog=this.querySelector('[data-contact-dialog]');dialog.showModal();return;}
    if(event.target.closest('[data-contact-close]')){this.querySelector('[data-contact-dialog]').close();return;}
    const choice=event.target.closest('[data-pdp-variant]');
    if(!choice || !this.contains(choice))return;
    event.preventDefault();
    const variant=choice.dataset.pdpVariant;
    if(!variant)return;
    if(this.adding){this.queuedChoice=choice;this.setPendingOption(choice);return;}
    if(variant===this.dataset.variantId){this.request?.abort();this.requesting=false;this.removeAttribute('aria-busy');this.setPendingOption(null);return;}
    const {url,fetchUrl}=this.optionUrls(variant);
    this.request?.abort();const request=this.request=new AbortController();
    this.requesting=true;this.setAttribute('aria-busy','true');this.setPendingOption(choice);
    try {
      const html=await this.optionHtml(fetchUrl);
      if(request.signal.aborted||this.request!==request||!this.isConnected)return;
      const document=new DOMParser().parseFromString(html,'text/html');
      const replacement=document.querySelector('napoleon-pdp-v2');
      if(!replacement || replacement.dataset.variantId!==variant)throw Error('Unexpected variant response');
      // Selection restoration must be restricted to accessories rendered for the new variant.
      const selected=new Set([...this.querySelectorAll('[data-addon-sku]:checked')].map(el=>el.dataset.addonSku));
      replacement.querySelectorAll('[data-addon-sku]').forEach(el=>{el.checked=selected.has(el.dataset.addonSku)});
      const focusKey=choice.dataset.optionAxis;
      // Capture immediately before replacement so scrolling during the request is respected.
      const anchorTop=choice.getBoundingClientRect().top,scrollX=window.scrollX,scrollY=window.scrollY;
      this.replaceWith(replacement);history.replaceState(history.state,'',url);
      const nextChoice=replacement.querySelector(`[data-option-axis="${CSS.escape(focusKey || '')}"][aria-pressed="true"]`);
      nextChoice?.focus({preventScroll:true});
      // Maintain the selected control's viewport position despite changed title/gallery heights.
      const nextY=nextChoice?window.scrollY+nextChoice.getBoundingClientRect().top-anchorTop:scrollY;
      window.scrollTo({left:scrollX,top:nextY,behavior:'instant'});
      if(nextChoice)replacement.preserveOptionPosition(nextChoice,anchorTop);
      replacement.dispatchEvent(new CustomEvent('napoleon:variant-change',{bubbles:true,detail:{variantId:variant}}));
    } catch(error) {
      if(error.name==='AbortError'||request.signal.aborted||this.request!==request)return;
      const notice=this.querySelector('[data-pdp-status]');
      if(notice)notice.textContent='This configuration could not be loaded. Please try again.';
    } finally {if(this.isConnected && this.request===request){this.requesting=false;this.removeAttribute('aria-busy');this.setPendingOption(null);}}
  }
});
